'use client'

import { createContext, useContext, ReactNode, useState, useEffect, useCallback, useMemo } from 'react'

interface LayoutContextType {
  brand?: any
  setBrand?: (brand: any) => void
  globalData?: any
  setGlobalData?: (data: any) => void
  loginEl?: any
  setLoginEl?: (loginEl: any) => void
  headerMenuJson?: any
  setHeaderMenuJson?: (menu: any) => void
  footerJson?: any
  setFooterJson?: (footer: any) => void
  pageInserts?: any
  setPageInserts?: (inserts: any) => void
  env?: string
  setEnv?: (env: string) => void
  searchBarOpen: boolean
  setSearchBarOpen: (open: boolean) => void
  query: string
  setQuery: (query: string) => void
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined)

interface LayoutProviderProps {
  children: ReactNode
  initialData?: Partial<LayoutContextType>
}

export function LayoutProvider({ children, initialData }: LayoutProviderProps) {
  const [searchBarOpen, setSearchBarOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [loginEl, setLoginEl] = useState(null)
  const [brand, setBrandState] = useState(initialData?.brand)
  const [globalData, setGlobalDataState] = useState(initialData?.globalData)
  const [headerMenuJson, setHeaderMenuJsonState] = useState(initialData?.headerMenuJson)
  const [footerJson, setFooterJsonState] = useState(initialData?.footerJson)
  const [pageInserts, setPageInsertsState] = useState(initialData?.pageInserts)
  const [env, setEnvState] = useState(initialData?.env)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedQuery = sessionStorage.getItem('userSearchQuery') || ''
      setQuery(savedQuery)
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('userSearchQuery', query)
    }
  }, [query])

  // Note: initialData is used only for initial useState values (above).
  // Runtime updates (e.g. vendor-specific brand, header, footer) are applied
  // exclusively via explicit setter calls from PageDataUpdater.
  // Do NOT sync initialData here — it would overwrite vendor-specific values
  // with the root layout's default nestingale data on every re-render.

  // Memoize setters to prevent unnecessary re-renders
  const setBrand = useCallback((newBrand: any) => {
    setBrandState(prev => {
      // Only update if actually different
      if (JSON.stringify(prev) !== JSON.stringify(newBrand)) {
        return newBrand
      }
      return prev
    })
  }, [])

  const setGlobalData = useCallback((data: any) => {
    setGlobalDataState(data)
  }, [])

  const setHeaderMenuJson = useCallback((menu: any) => {
    setHeaderMenuJsonState(menu)
  }, [])

  const setFooterJson = useCallback((footer: any) => {
    setFooterJsonState(footer)
  }, [])

  const setPageInserts = useCallback((inserts: any) => {
    setPageInsertsState(inserts)
  }, [])

  const setEnv = useCallback((newEnv: string) => {
    setEnvState(newEnv)
  }, [])

  const value: LayoutContextType = useMemo(() => ({
    brand,
    setBrand,
    globalData,
    setGlobalData,
    loginEl,
    setLoginEl,
    headerMenuJson,
    setHeaderMenuJson,
    footerJson,
    setFooterJson,
    pageInserts,
    setPageInserts,
    env,
    setEnv,
    searchBarOpen,
    setSearchBarOpen,
    query,
    setQuery,
  }), [brand, setBrand, globalData, setGlobalData, loginEl, setLoginEl, headerMenuJson, setHeaderMenuJson, footerJson, setFooterJson, pageInserts, setPageInserts, env, setEnv, searchBarOpen, setSearchBarOpen, query, setQuery])

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  )
}

export function useLayout() {
  const context = useContext(LayoutContext)
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider')
  }
  return context
}